import { useMemo, useRef } from "react"
import { Link } from "react-router-dom"
import { ExternalLink } from "lucide-react"
import { CellContext, PaginationState } from "@tanstack/react-table"
import Table from "../table"
import DateFilter, {
  DateFilterHandle,
  DateRangePreset
} from "../activity/date-filter"
import {
  AdvisorSession,
  BBColumnDef,
  SubscriptionDetails
} from "../../../types"
import { formatAdvisorDate } from "../../utils/datetime"

interface Props {
  sessions: AdvisorSession[]
  subscriptionMap: Record<string, SubscriptionDetails>
  brandMap: Record<string, { name: string }>
  isBrandsLoading: boolean
  isLoading: boolean
  totalRows: number
  pagination: PaginationState
  setPagination: React.Dispatch<React.SetStateAction<PaginationState>>
  pageCount: number
  searchQuery: string
  onSearchChange: (value: string) => void
  datePreset: DateRangePreset
  customDateStart: number | null
  customDateEnd: number | null
  onDateChange: (
    range: DateRangePreset,
    startDate?: Date,
    endDate?: Date
  ) => void
  onClearDate: () => void
}

const AdvisorSessionsTable = ({
  sessions,
  subscriptionMap,
  brandMap,
  isBrandsLoading,
  isLoading,
  totalRows,
  pagination,
  setPagination,
  pageCount,
  searchQuery,
  onSearchChange,
  datePreset,
  customDateStart,
  customDateEnd,
  onDateChange,
  onClearDate
}: Props) => {
  const dateFilterRef = useRef<DateFilterHandle>(null)

  const columns = useMemo<BBColumnDef<AdvisorSession>[]>(
    () => [
      {
        id: "leadName",
        header: "Lead Name",
        accessorFn: row => {
          const sub = row.subscriptionId
            ? subscriptionMap[row.subscriptionId]
            : undefined
          return sub
            ? `${sub.first_name ?? ""} ${sub.last_name ?? ""} ${
                sub.email ?? ""
              }`
            : ""
        },
        cell: ({ row }: CellContext<AdvisorSession, unknown>) => {
          const sub = row.original.subscriptionId
            ? subscriptionMap[row.original.subscriptionId]
            : undefined
          return (
            <td className="py-4 pl-2 pr-3">
              {sub ? (
                <>
                  <div className="font-semibold">
                    {`${sub.first_name ?? ""} ${sub.last_name ?? ""}`.trim() ||
                      "—"}
                  </div>
                  <div className="text-sm text-neutral-400 truncate max-w-xs">
                    {sub.email ?? ""}
                  </div>
                  {isBrandsLoading ? (
                    <div className="h-3 w-24 rounded bg-neutral-100 animate-pulse mt-1" />
                  ) : (
                    brandMap[row.original.brandId ?? ""]?.name && (
                      <div className="text-xs text-neutral-400 truncate max-w-xs mt-0.5">
                        {brandMap[row.original.brandId ?? ""]!.name}
                      </div>
                    )
                  )}
                </>
              ) : (
                <div className="space-y-1.5">
                  <div className="h-3.5 w-32 rounded bg-neutral-100 animate-pulse" />
                  <div className="h-3 w-44 rounded bg-neutral-100 animate-pulse" />
                </div>
              )}
            </td>
          )
        }
      },
      {
        id: "lastActivity",
        header: "Last Activity",
        accessorFn: row => row.lastActivityAt,
        cell: ({ row }: CellContext<AdvisorSession, unknown>) => (
          <td className="py-4 pr-3 text-sm text-neutral-500 whitespace-nowrap">
            {formatAdvisorDate(row.original.lastActivityAt)}
          </td>
        ),
        meta: { headerClassName: "hidden md:table-cell" }
      },
      {
        id: "threads",
        header: "Threads #",
        accessorFn: row => row.threads.length,
        cell: ({ row }: CellContext<AdvisorSession, unknown>) => (
          <td className="py-4 pr-3 text-sm text-neutral-500">
            {row.original.threads.length}
          </td>
        ),
        meta: { headerClassName: "hidden md:table-cell" },
        enableSorting: true
      },
      {
        id: "action",
        header: "",
        enableSorting: false,
        meta: { headerClassName: "w-12 sr-only" },
        cell: ({ row }: CellContext<AdvisorSession, unknown>) => (
          <td className="px-5 py-3 text-right">
            <Link
              target="_blank"
              to={`/conversations/advisor/${row.original._id}`}
              className="inline-flex text-neutral-400 hover:text-purple-400 transition-colors"
              title="Open in Conversations"
            >
              <ExternalLink className="w-4 h-4" />
            </Link>
          </td>
        )
      }
    ],
    [subscriptionMap, brandMap, isBrandsLoading]
  )

  return (
    <Table<AdvisorSession>
      isLoading={isLoading}
      data={sessions}
      columns={columns}
      viewType="table"
      searchPlaceholder="Search by name, email, or brand…"
      pagination={pagination}
      setPagination={setPagination}
      pageCount={pageCount}
      rowCount={totalRows}
      onSearchChange={onSearchChange}
      pageSize={pagination.pageSize}
      extraHeaderElement={
        <DateFilter
          ref={dateFilterRef}
          selectedRange={datePreset}
          customStartDate={customDateStart ? new Date(customDateStart) : null}
          customEndDate={customDateEnd ? new Date(customDateEnd) : null}
          onRangeChange={onDateChange}
          onClearFilter={onClearDate}
        />
      }
      extraHeaderElementClassName="ml-auto"
      shrinkFilterSort
      defaultSortBy="lastActivity"
      defaultSortDir="desc"
      noSorter={false}
    />
  )
}

export default AdvisorSessionsTable
